/**
 * DynamicForm — Schema-driven form that auto-generates from Directus field metadata.
 *
 * When a field is added in the Directus Data Studio, it appears here automatically.
 * No code changes needed.
 *
 * Features:
 * - Reads field metadata via useSchema()
 * - Renders fields sorted by meta.sort
 * - Validates with Zod schemas built from field metadata
 * - Supports create, edit, and read-only (view) modes
 * - Two-column grid layout respecting meta.width
 * - React Hook Form for state management
 */

import * as React from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { DirectusField } from '@yesbos/api-client';
import { useSchema } from '@yesbos/hooks';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { FieldWrapper, formatLabel } from './field-renderer';
import { cn } from '../../lib/utils';
import { Loader2 } from 'lucide-react';

// ─── Form Mode ─────────────────────────────────────────────────────

export type FormMode = 'create' | 'edit' | 'view';

// ─── Zod Schema Builder ────────────────────────────────────────────

/**
 * Build a Zod validation schema from Directus field metadata.
 * Maps field types + validation rules → Zod validators.
 */
function buildZodSchema(fields: DirectusField[]): z.ZodObject<any> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    let validator: z.ZodTypeAny;
    const isRequired = !!field.meta?.required;
    const label = formatLabel(field.field);

    // Determine if this is a string-like type (string, text, uuid, etc.)
    const isStringType = ['string', 'text', 'uuid', 'csv', 'hash'].includes(field.type);
    const isNumericType = ['integer', 'bigInteger', 'float', 'decimal'].includes(field.type);
    const isDateType = ['date', 'dateTime', 'timestamp'].includes(field.type);

    if (field.type === 'boolean') {
      validator = z.boolean().optional();
    } else if (field.type === 'json') {
      validator = z.any();
    } else if (isNumericType) {
      if (isRequired) {
        let numValidator = z.number({ required_error: `${label} is required` });
        if (['integer', 'bigInteger'].includes(field.type)) {
          numValidator = numValidator.int();
        }
        validator = numValidator;
      } else {
        let numValidator = z.number();
        if (['integer', 'bigInteger'].includes(field.type)) {
          numValidator = numValidator.int();
        }
        validator = numValidator.nullable().optional();
      }
    } else if (isStringType || isDateType) {
      // Build a string validator, apply max_length BEFORE wrapping with nullable
      let strValidator = z.string();

      if (isRequired) {
        strValidator = z.string({ required_error: `${label} is required` })
          .min(1, `${label} is required`);
      }

      // Apply max_length on the base ZodString (before nullable)
      if (field.schema?.max_length && isStringType) {
        strValidator = strValidator.max(
          field.schema.max_length,
          `Maximum ${field.schema.max_length} characters`,
        );
      }

      // Wrap with nullable/optional if not required
      if (isRequired) {
        validator = strValidator;
      } else {
        validator = strValidator.nullable().optional();
      }
    } else {
      // Fallback for unknown types
      validator = isRequired
        ? z.string({ required_error: `${label} is required` }).min(1, `${label} is required`)
        : z.string().nullable().optional();
    }

    shape[field.field] = validator;
  }

  return z.object(shape);
}

// ─── Props ─────────────────────────────────────────────────────────

export interface DynamicFormProps {
  /** Directus collection name (e.g., "employees") */
  collection: string;
  /** Form mode — create, edit, or view (read-only) */
  mode: FormMode;
  /** Initial values for edit/view mode */
  initialValues?: Record<string, unknown>;
  /** Submit handler */
  onSubmit?: (values: Record<string, unknown>) => void | Promise<void>;
  /** Cancel handler */
  onCancel?: () => void;
  /** Custom title (defaults to collection name) */
  title?: string;
  /** Loading state (for async submit) */
  isSubmitting?: boolean;
  /** Additional fields to exclude */
  excludeFields?: string[];
  /** Custom class name for the form container */
  className?: string;
}

// ─── Component ─────────────────────────────────────────────────────

export function DynamicForm({
  collection,
  mode,
  initialValues = {},
  onSubmit,
  onCancel,
  title,
  isSubmitting = false,
  excludeFields = [],
  className,
}: DynamicFormProps) {
  const { sortedFields, isLoading, isError, error } = useSchema(collection);

  // Filter out excluded fields
  const visibleFields = React.useMemo(
    () => sortedFields.filter((f) => !excludeFields.includes(f.field)),
    [sortedFields, excludeFields],
  );

  // Build Zod schema from visible fields
  const zodSchema = React.useMemo(
    () => (visibleFields.length > 0 ? buildZodSchema(visibleFields) : z.object({})),
    [visibleFields],
  );

  // React Hook Form
  const form = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: initialValues as Record<string, any>,
    mode: 'onBlur',
  });

  // Reset form when initialValues change (edit mode navigation)
  React.useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      form.reset(initialValues as Record<string, any>);
    }
  }, [initialValues, form]);

  // Handle submit
  const handleSubmit = form.handleSubmit(async (data) => {
    if (onSubmit) {
      await onSubmit(data);
    }
  });

  // ─── Loading State ─────────────────────────────────────────────

  if (isLoading) {
    return (
      <Card className={cn('w-full', className)}>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--color-primary)]" />
          <span className="ml-3 text-sm text-[var(--color-muted-foreground)]">
            Loading form fields...
          </span>
        </CardContent>
      </Card>
    );
  }

  // ─── Error State ───────────────────────────────────────────────

  if (isError) {
    return (
      <Card className={cn('w-full', className)}>
        <CardContent className="py-8">
          <p className="text-sm text-[var(--color-destructive)]">
            Failed to load form schema: {error?.message ?? 'Unknown error'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ─── Empty State ───────────────────────────────────────────────

  if (visibleFields.length === 0) {
    return (
      <Card className={cn('w-full', className)}>
        <CardContent className="py-8">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            No fields found for collection "{collection}".
          </p>
        </CardContent>
      </Card>
    );
  }

  // ─── Form Title ────────────────────────────────────────────────

  const formTitle =
    title ??
    `${mode === 'create' ? 'Add' : mode === 'edit' ? 'Edit' : 'View'} ${formatLabel(collection)}`;

  const isReadOnly = mode === 'view';

  // ─── Render ────────────────────────────────────────────────────

  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <CardTitle className="text-xl">{formTitle}</CardTitle>
      </CardHeader>

      <form onSubmit={handleSubmit}>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {visibleFields.map((field) => (
              <FieldWrapper
                key={field.field}
                field={field}
                value={form.watch(field.field)}
                onChange={(val) => {
                  form.setValue(field.field, val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                error={form.formState.errors[field.field]?.message as string}
                readOnly={isReadOnly}
                disabled={isSubmitting}
              />
            ))}
          </div>
        </CardContent>

        {!isReadOnly && (
          <CardFooter className="flex justify-end gap-3 border-t border-[var(--color-border)] pt-6">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {mode === 'create' ? 'Create' : 'Save Changes'}
            </Button>
          </CardFooter>
        )}
      </form>
    </Card>
  );
}
